/**
 * 설정 파일을 가져오기 위한 공통 유틸리티 함수들
 */

/**
 * 서버 URL을 기반으로 올바른 base URL을 생성합니다.
 * @param serverUrl - 환경변수에서 가져온 서버 URL
 * @returns 올바른 base URL
 */
export function getBaseUrl(serverUrl: string | undefined): string {
  const defaultServerUrl = "custom";
  const actualServerUrl = serverUrl || defaultServerUrl;

  // serverUrl이 "custom"인 경우 "/custom/" 경로를 사용
  return actualServerUrl === "custom" ? "/custom" : actualServerUrl;
}

/**
 * HTTP 응답이 유효한지 검증합니다.
 * @param response - fetch 응답 객체
 * @param fileName - 파일명 (에러 메시지용)
 * @throws Error - 응답이 유효하지 않은 경우
 */
export function validateResponse(response: Response, fileName: string): void {
  // 응답 상태 확인
  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${fileName}: ${response.status} ${response.statusText}`,
    );
  }

  // Content-Type 확인
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    throw new Error(`${fileName} response is not JSON format: ${contentType}`);
  }
}

/**
 * 설정 파일을 안전하게 가져옵니다.
 * @param fileName - 가져올 파일명 (예: "EnvVars.json")
 * @param serverUrl - 서버 URL (선택사항)
 * @returns JSON 데이터
 * @throws Error - 파일을 가져오거나 파싱하는데 실패한 경우
 */
export async function fetchConfigFile<T = any>(
  fileName: string,
  serverUrl?: string,
): Promise<T> {
  const baseUrl = getBaseUrl(serverUrl);
  const url = `${baseUrl}/${fileName}`;

  const response = await fetch(url);
  validateResponse(response, fileName);

  return await response.json();
}

/**
 * 설정 파일을 가져오되, 실패 시 기본값을 반환합니다.
 * @param fileName - 가져올 파일명
 * @param defaultData - 실패 시 반환할 기본값
 * @param serverUrl - 서버 URL (선택사항)
 * @returns JSON 데이터 또는 기본값
 */
export async function fetchConfigFileWithDefault<T = any>(
  fileName: string,
  defaultData: T,
  serverUrl?: string,
): Promise<T> {
  try {
    return await fetchConfigFile<T>(fileName, serverUrl);
  } catch (error) {
    console.error(`Error fetching ${fileName}:`, error);
    return defaultData;
  }
}

/**
 * 환경 변수 객체의 값들을 Store에 설정하는 헬퍼 함수
 * @param envVars - 환경 변수 객체
 * @param setters - 설정 함수들의 객체
 */
export function applyEnvVars(
  envVars: Record<string, any>,
  setters: Record<string, (value: any) => void>,
): void {
  Object.entries(envVars).forEach(([key, value]) => {
    if (value && setters[key]) {
      setters[key](value);
    }
  });
}
